import { Component, ElementRef, QueryList, ViewChildren, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';

import { RegistrationFormService } from '../../../services/registration-form.service';

@Component({
  selector: 'app-public-pss-registration',
  standalone: true,
  imports: [ReactiveFormsModule, RouterLink],
  templateUrl: './public-pss-registration.component.html',
  styleUrls: ['./public-pss-registration.component.scss']
})
export class PublicPssRegistrationComponent {
  private readonly fb = inject(FormBuilder);
  private readonly formService = inject(RegistrationFormService);
  private readonly router = inject(Router);
  @ViewChildren('fileInput') private fileInputs!: QueryList<ElementRef<HTMLInputElement>>;

  message = '';
  messageType: 'success' | 'error' | '' = '';
  readonly form = this.fb.nonNullable.group({
    pss_owner_name: ['', [Validators.required]],
    organization_entity: ['', [Validators.required]],
    pss_name: ['', [Validators.required]],
    pss_code: ['', [Validators.required]],
    voltage_level: ['', [Validators.required]],
    grid_interconnection_type: ['', [Validators.required]],
    address_line1: ['', [Validators.required]],
    address_line2: [''],
    city_town: ['', [Validators.required]],
    district: ['', [Validators.required]],
    state: ['', [Validators.required]],
    pin_code: ['', [Validators.required]],
    country: ['India', [Validators.required]],
    contact_person_name: ['', [Validators.required]],
    designation: [''],
    mobile_number: ['', [Validators.required]],
    email_address: ['', [Validators.required, Validators.email]],
    document_one: [''],
    document_two: [''],
    document_three: [''],
    declaration_accept: [false, [Validators.requiredTrue]]
  });

  readonly documents = [
    'PSS Ownership / Asset Certificate',
    'Single Line Diagram (SLD)',
    'Asset Registration from SLDC/CTU'
  ];
  readonly uploadSlots = this.documents.map((name) => ({ name, files: [] as File[] }));

  openFilePicker(index: number): void {
    this.fileInputs?.get(index)?.nativeElement.click();
  }

  onFilesSelected(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    this.uploadSlots[index].files = Array.from(input.files ?? []);
  }

  private buildUsername(entityName: string): string {
    const slug = entityName.toLowerCase().replace(/[^a-z0-9]+/g, '.').replace(/^\.+|\.+$/g, '');
    return `${slug || 'pss'}-${Date.now().toString(36)}`;
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        if (typeof result === 'string') {
          resolve(result.split(',')[1] || '');
          return;
        }
        reject(new Error('Unable to read file.'));
      };
      reader.onerror = () => reject(new Error('Unable to read file.'));
      reader.readAsDataURL(file);
    });
  }

  async submit(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.message = 'Please fix the highlighted fields before submitting.';
      this.messageType = 'error';
      return;
    }

    const raw = this.form.getRawValue();
    const entityName = raw.pss_owner_name || raw.pss_name || 'PSS Registration';
    const documents = await Promise.all(this.uploadSlots.flatMap((slot) =>
      slot.files.map(async (file) => ({
        document_type: slot.name,
        file_name: file.name,
        file_type: file.type || 'application/octet-stream',
        file_data_base64: await this.fileToBase64(file)
      }))
    ));

    this.formService.submit({
      entity_type: 'PSS_OWNER',
      entity_name: entityName,
      username: this.buildUsername(entityName),
      email: raw.email_address || '',
      mobile: raw.mobile_number || null,
      payload_json: {
        ...raw,
        category: 'PSS',
        documents: this.documents
      },
      documents,
      submitted_by: null
    }).subscribe({
      next: (response) => {
        this.message = 'PSS owner registration submitted successfully.';
        this.messageType = 'success';
        void this.router.navigate(['/register/pss/confirmation'], {
          state: {
            registrationNo: response.value === '' ? 'N/A' : response.value.registration_no,
            stakeholderType: 'PSS Owner',
            submittedOn: new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }),
            status: 'Pending Admin Review'
          }
        });
      },
      error: () => {
        this.message = 'Unable to submit PSS owner registration.';
        this.messageType = 'error';
      }
    });
  }
}

